import React, { useState } from "react";
import type { TranscriptItem } from "../../translator/hooks/useTranslator";
import type { Meeting } from "../hooks/useMeetingHistory";
import { useUiLang } from "../../../context/UiLangContext";
import { Calendar } from "lucide-react";
import { useMeetingHistory } from "../hooks/useMeetingHistory";

interface TranslationHistoryProps {
  translatorServiceUrl: string;
  recording: boolean;
  onOpenSavedSummary: (meeting: Meeting, summaryText: string, htmlFilename?: string) => void;
}

export const TranslationHistory: React.FC<TranslationHistoryProps> = ({
  translatorServiceUrl,
  recording,
  onOpenSavedSummary,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const { meetings: pastMeetings, loading, loadingMeetingId, loadMeetingSummary, fetchMeetings, error, setError } = useMeetingHistory(translatorServiceUrl);

  React.useEffect(() => {
    fetchMeetings("", isEn);
    const onMeetingsChanged = () => fetchMeetings("", isEn, true);
    window.addEventListener("kira:meetings-changed", onMeetingsChanged);
    return () => window.removeEventListener("kira:meetings-changed", onMeetingsChanged);
  }, [fetchMeetings, isEn]);

  const prevRecordingRef = React.useRef(recording);
  React.useEffect(() => {
    if (prevRecordingRef.current && !recording) {
      setTimeout(() => fetchMeetings("", isEn), 1000);
    }
    prevRecordingRef.current = recording;
  }, [recording, fetchMeetings, isEn]);

  const handleLoad = async (meeting: Meeting) => {
    await loadMeetingSummary(meeting, recording, isEn, onOpenSavedSummary);
  };

  const formatDate = (isoString: string) => {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const timeStr = d.toLocaleTimeString(isEn ? "en-US" : "es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });

    if (targetDate.getTime() === today.getTime()) {
      return `${isEn ? "Today" : "Hoy"}, ${timeStr}`;
    } else if (targetDate.getTime() === yesterday.getTime()) {
      return `${isEn ? "Yesterday" : "Ayer"}, ${timeStr}`;
    } else {
      const dateStr = d.toLocaleDateString(isEn ? "en-US" : "es-ES", {
        day: "numeric",
        month: "short"
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  const formatDuration = (meeting: Meeting) => {
    const durVal = meeting.duration ?? meeting.duration_seconds ?? meeting.duration_ms ?? meeting.dur ?? meeting.segs ?? meeting.length;
    if (durVal !== undefined && durVal !== null && durVal !== "") {
      if (typeof durVal === "string" && (durVal.includes("min") || durVal.includes("seg") || durVal.includes("sec"))) {
        return durVal;
      }
      if (typeof durVal === "string" && durVal.includes(":")) {
        const parts = durVal.split(":");
        if (parts.length === 2) {
          const minsStr = (parts[0] || "00").padStart(2, "0");
          const secsStr = (parts[1] || "00").padStart(2, "0");
          return `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
        }
        if (parts.length === 3) {
          const hrsStr = (parts[0] || "00").padStart(2, "0");
          const minsStr = (parts[1] || "00").padStart(2, "0");
          const secsStr = (parts[2] || "00").padStart(2, "0");
          return parseInt(hrsStr) > 0
            ? `${hrsStr} h ${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`
            : `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
        }
      }
      const numSec = Number(durVal);
      if (!isNaN(numSec) && numSec >= 0) {
        const totalSecs = numSec > 50000 ? Math.floor(numSec / 1000) : numSec;
        const minsStr = Math.floor(totalSecs / 60).toString().padStart(2, "0");
        const secsStr = Math.floor(totalSecs % 60).toString().padStart(2, "0");
        return `${minsStr} min ${isEn ? "and" : "y"} ${secsStr} seg`;
      }
    }
    return `00 min ${isEn ? "and" : "y"} 00 seg`;
  };

  return (
    <div className="w-full lg:w-[250px] flex-shrink-0 flex flex-col bg-[var(--bg2)] border-b lg:border-b-0 lg:border-r border-[var(--b1)] p-[16px] overflow-hidden max-h-[35vh] lg:max-h-none order-2 lg:order-1 transition-colors duration-300">
      <div className="flex items-center justify-between mb-[12px] px-[4px]">
        <span className="text-[10px] font-bold tracking-[0.1em] text-[var(--text-dim)] uppercase">
          {isEn ? "Meeting History" : "Historial de minutas"}
        </span>
        <span className="text-[10px] font-medium text-[var(--text-dim)] bg-[var(--pill)] border border-[var(--b1)] px-[7px] py-[1px] rounded-full">
          {pastMeetings.length}
        </span>
      </div>

      {error && (
        <div className="mb-[10px] p-[10px] rounded-[8px] bg-[var(--danger)]/10 border border-[var(--danger)]/25 text-[11px] text-[var(--danger)] flex items-start gap-[6px] transition-all">
          <span className="font-bold flex-shrink-0 mt-[1px]">⚠</span>
          <p className="leading-tight flex-1 font-medium">{error}</p>
          <button onClick={() => setError("")} className="text-[var(--danger)] hover:opacity-75 font-bold cursor-pointer ml-auto">✕</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-[8px] pr-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4 gap-3">
            <div className="w-6 h-6 border-2 border-[var(--accent)]/30 border-t-[var(--accent)] rounded-full animate-spin" />
            <p className="text-[12px] text-[var(--text-dim)] font-medium">
              {isEn ? "Fetching your meetings..." : "Obteniendo tus minutas..."}
            </p>
          </div>
        ) : pastMeetings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <Calendar className="w-8 h-8 text-[var(--text-dim)] opacity-50 mb-3" />
            <p className="text-[12px] text-[var(--text-dim)] font-medium">
              {isEn ? "Record your first meeting to see it here." : "Grabá tu primera reunión para verla aquí."}
            </p>
          </div>
        ) : (
          pastMeetings.map((meeting) => (
              <div
                key={meeting._id}
                onClick={() => handleLoad(meeting)}
                className="py-[9px] px-[11px] bg-[var(--bg3)] hover:bg-[var(--bg4)] border border-[var(--b1)] hover:border-[var(--b2)] rounded-[8px] cursor-pointer transition-all duration-200 flex flex-col gap-[4px] relative"
                role="button"
                tabIndex={0}
              >
                {loadingMeetingId === meeting._id && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center rounded-[8px] z-10">
                    <div className="w-4 h-4 border-2 border-accent/20 border-t-accent rounded-full animate-spin" />
                  </div>
                )}
                {/* Top: Date and Badge */}
                <div className="flex justify-between items-center gap-[6px]">
                  <span className="text-[9.5px] text-[var(--text-dim)] tabular-nums">{formatDate(meeting.created_at)}</span>
                  {meeting.mode === "minuta" ? (
                    <span className="text-[8.5px] font-bold tracking-[0.04em] px-[6px] py-[1px] rounded-full whitespace-nowrap bg-[var(--badge-m-bg)] text-[var(--badge-m-text)]">M</span>
                  ) : (
                    <span className="text-[8.5px] font-bold tracking-[0.04em] px-[6px] py-[1px] rounded-full whitespace-nowrap bg-[var(--badge-tm-bg)] text-[var(--badge-tm-text)]">T+M</span>
                  )}
                </div>
                {/* Middle: Title */}
                <div className="text-[12px] font-medium text-[var(--text)] leading-snug line-clamp-2">
                  {meeting.title || (isEn ? "Untitled Meeting" : "Reunión sin título")}
                </div>
                {/* Bottom: Duration */}
                <div className="text-[9.5px] text-[var(--text-dim)]">
                  {formatDuration(meeting)}
                </div>
              </div>
          ))
        )}
      </div>
    </div>
  );
};
